import styled from 'styled-components';
import { Grid, Type } from '../../../app/StyleGuide';

export const PrintColors = {
  Green: '#08a2a3',
  LightGreen: '#daf1f1',
  DarkGray: '#333333',
  LightGray: '#9d9d9d',
};

export const FONT_SIZE_SCALE = {
  10: {
    base: '10px',
    h1: '15px',
    h2: '13px',
    h3: '11px',
    h4: '10px',
    h5: '9px',
    h6: '8px',
    body: '10px',
    small: '8px',
    tiny: '7px',
  },
  12: {
    base: '12px',
    h1: '18px',
    h2: '16px',
    h3: '14px',
    h4: '13px',
    h5: '12px',
    h6: '11px',
    body: '12px',
    small: '10px',
    tiny: '9px',
  },
  14: {
    base: '14px',
    h1: '21px',
    h2: '19px',
    h3: '16px',
    h4: '15px',
    h5: '14px',
    h6: '13px',
    body: '14px',
    small: '12px',
    tiny: '11px',
  },
  16: {
    base: '16px',
    h1: '24px',
    h2: '22px',
    h3: '19px',
    h4: '18px',
    h5: '16px',
    h6: '15px',
    body: '16px',
    small: '14px',
    tiny: '13px',
  },
};

export const PrintSettings = {
  borderRadius: Grid._4,
};

export const PrintHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 2px solid ${PrintColors.Green};
  align-items: center;
  padding: ${Grid._3} ${Grid._3};
  margin-bottom: ${Grid._3};
`;

export const PrintTransition = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  background-color: #f4f4f4;
  padding: ${Grid._3} ${Grid._3};
  align-items: center;
  margin: ${Grid._3} 0;

  @media print {
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    background-color: #f4f4f4 !important;
  }
`;

export const PrintSubTitle = styled.h3<{ color?: string; fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-weight: 900;
  color: ${props => props.color ?? PrintColors.DarkGray};
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h3} !important;

  span {
    color: ${PrintColors.LightGray};
    font-weight: 400;
    text-transform: none;
  }
`;

export const PrintIcon = styled.i<{ color: string; fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h4};
  color: ${props => props.color};
`;

export const SegmentHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

export const PrintParagraph = styled.p`
  font-weight: normal;
  color: black;
  margin: 0;
`;

export const Chip = styled.div<{ color: string; fontSize: keyof typeof FONT_SIZE_SCALE }>`
  padding: ${Grid._1} ${Grid._4};
  border-radius: ${PrintSettings.borderRadius};
  background-color: ${props => props.color};

  p {
    margin: 0;
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].small};
  }

  @media print {
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    background-color: ${props => props.color} !important;
  }
`;

export const PrintMediaSlide = styled.img`
  width: 30%;
  height: auto;
  aspect-ratio: 16/9;
  object-fit: cover;
  border-radius: ${PrintSettings.borderRadius};
`;

export const PrintMediaSlideContainer = styled.div`
  width: 30%;
  height: auto;
  aspect-ratio: 16/9;
  object-fit: cover;
  border-radius: ${PrintSettings.borderRadius};
`;

export const VideoSectionContainer = styled.div<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  i {
    color: ${Type.Color.medium};
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h1};
  }
  h5 {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h4};
    z-index: 1;
    padding: ${Grid._3} ${Grid._4} ${Grid._4} ${Grid._4};
  }
  p {
    margin: 0;
  }
`;

export const BodyContentContainer = styled.div<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].base};

  h1 {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h1} !important;
    margin: 0;
  }

  h2 {
    font-size: 10 !important;
    margin: 0;
  }

  h3 {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h3} !important;
    margin: 0;
  }

  h4 {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h4} !important;
    margin: 0;
  }

  h5 {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h5} !important;
    margin: 0;
  }

  h6 {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h6} !important;
    margin: 0;
  }

  p,
  span,
  div,
  li,
  dt,
  dd {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body} !important;
  }

  ul,
  ol {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body};

    li {
      font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body} !important;
    }
  }

  ul ul,
  ol ol,
  ul ol,
  ol ul {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body};

    li {
      font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body} !important;
    }
  }

  blockquote,
  pre,
  code,
  strong,
  em,
  b,
  i,
  a {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body} !important;
  }

  small,
  .small {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].small} !important;
  }

  .tiny {
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].tiny} !important;
  }

  width: 100%;
`;
